import { NgClass } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, OnInit, untracked } from '@angular/core';
import { Auth, PasswordPolicy, PasswordValidationStatus, validatePassword } from '@angular/fire/auth';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIconsModule } from '@ng-icons/core';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { derivedAsync } from 'ngxtension/derived-async';
import { injectParams } from 'ngxtension/inject-params';
import { debounceTime, Subject, Subscription, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Tenant } from 'src/environments/evironment.interface';
import { AuthenticationService } from 'src/shared/services/auth/auth_service';
import { MixpanelService } from 'src/shared/services/mix-panel.service/mix-panel.service';
import { ValidationService } from 'src/shared/services/validation/validation.service';
import { passwordPolicyValidator } from 'src/shared/services/validation/validators/password-policy.validator';
import { BannerComponent } from '../../design/banner/banner.component';
import { CenterTextButton } from '../../design/centertextbutton/centertextbutton.component';
import { RightImagePageWrapperComponent } from '../../design/rightimagepagewrapper/rightimagepagewrapper.component';
import { TitleComponent } from '../../design/title/title.component';
import { FormSubmissionButtonComponent } from '../../forms/form-submission-button/form-submission-button.component';
import { InputComponent } from '../../forms/input/input.component';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  imports: [
    NgClass,
    TitleComponent,
    FormsModule,
    ReactiveFormsModule,
    BannerComponent,
    InputComponent,
    TranslateModule,
    CenterTextButton,
    FormSubmissionButtonComponent,
    CenterTextButton,
    NgIconsModule,
    RightImagePageWrapperComponent,
  ],
  providers: [TranslatePipe],
})
export class RegisterComponent implements OnInit, OnDestroy {
  route = inject(ActivatedRoute);
  router = inject(Router);
  fb = inject(FormBuilder);
  authenticationService = inject(AuthenticationService);
  mixPanelService = inject(MixpanelService);
  validationService = inject(ValidationService);
  translate = inject(TranslatePipe);
  firebaseAuth = inject(Auth);
  authValidatePassword = validatePassword;

  passwordValidationStatus = derivedAsync(() => this.authValidatePassword(this.firebaseAuth, ''));
  passwordPolicy = computed(() => this.passwordValidationStatus()?.passwordPolicy);
  isLengthValid = false;
  hasLowercase = false;
  hasUppercase = false;
  hasNumber = false;
  hasSpecialChar = false;
  subscription = new Subscription();
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.mixPanelService.track('account.create', { business: this.activeTenant?.friendly_id });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscription?.unsubscribe();
  }

  registerform!: FormGroup;
  loading!: boolean;
  error!: string;
  providerParam = injectParams('provider');
  activeTenant: Tenant | undefined = this.authenticationService.tenant();
  providerswitcher: boolean = environment.providerswitcher;
  showPassword: boolean = false;

  setActiveTenantEffect = effect(() => {
    const provider = this.providerParam();
    if (provider) {
      this.authenticationService.setTenant(provider);
    }
    untracked(() => {
      if (provider !== this.activeTenant?.friendly_id) {
        this.authenticationService.updateTenant();
        this.setForm(this.passwordPolicy());
      }
      if (provider !== this.authenticationService.getTenantById(provider ?? '')?.friendly_id) {
        this.authenticationService.setEntitlementProvider(provider ?? '');
        this.authenticationService.updateEntitlementProvider();
      }
    });
  });

  constructor() {
    effect(() => {
      this.setForm(this.passwordPolicy());
    });
  }

  async onSubmitRegister(form: FormGroup) {
    if (!form.valid) return;
    if (this.loading) return;
    this.error = '';
    this.loading = true;
    let status: PasswordValidationStatus | undefined = undefined;

    try {
      status = await this.authValidatePassword(this.firebaseAuth, form.value.password);
    } catch {
      console.error('Failed to validate password');
    }

    if (!status || !status?.isValid) {
      this.error = this.translate.transform('generic.password.validation.error');
      this.loading = false;
      return;
    }

    this.authenticationService
      .createAccount(form.value.email, form.value.password)
      .then(() => this.next())
      .catch((error) => this.handlerError(error.message))
      .finally(() => this.resetForm());
  }

  login() {
    const provider = this.providerParam();
    if (provider) {
      this.router.navigate(['/login', provider], { queryParamsHandling: 'preserve' });
    } else {
      this.router.navigate(['/login'], { queryParamsHandling: 'preserve' });
    }
  }

  anothertenant() {
    this.router.navigate(['/tenants/register'], { queryParamsHandling: 'preserve' });
  }

  next() {
    const url = this.route.snapshot.queryParamMap.get('redirectTo') ?? '';
    const decodedUrl = decodeURIComponent(url);
    if (decodedUrl) {
      this.redirectToUrl(decodedUrl);
    } else {
      this.router.navigate([environment.afterAuthPath]);
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  handlerError(error: string) {
    this.error = error;
  }

  clearError() {
    this.error = '';
  }

  resetForm() {
    this.loading = false;
  }

  google() {
    this.authenticationService
      .loginWithGoogle()
      .then(() => this.next())
      .catch((error) => this.handlerError(error.message))
      .finally(() => this.resetForm());
  }

  redirectToUrl(url: string) {
    const parsedUrl = new URL(url, window.location.origin);

    const route = parsedUrl.pathname;

    const queryParams: { [key: string]: string } = {};
    parsedUrl.searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    this.router.navigate([route], { queryParams });
  }

  setForm(passwordPolicy: PasswordPolicy | undefined) {
    if (!passwordPolicy) {
      console.warn('Password policy is undefined, setting form without validation.');
      this.registerform = this.fb.group({
        email: ['', [ValidationService.validateEmail]],
        password: ['', []],
      });
      return;
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = new Subscription();
    }
    this.registerform = this.fb.group({
      email: ['', [ValidationService.validateEmail]],
      password: ['', [passwordPolicy ? passwordPolicyValidator(passwordPolicy) : null]],
    });
    this.subscription.add(
      this.registerform.valueChanges.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe((value) => {
        this.onPasswordCheck(value.password || '');
      })
    );
  }

  escapeForRegex(characters?: string): string {
    if (!characters) return '';
    return characters.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  }

  onPasswordCheck(value: string): void {
    const opts = this.passwordPolicy()?.customStrengthOptions ?? {};
    const allowedChars = this.passwordPolicy()?.allowedNonAlphanumericCharacters ?? '';

    const minLen = opts.minPasswordLength ?? 0;
    this.isLengthValid = value.length >= minLen;
    this.hasLowercase = /[a-z]/.test(value);
    this.hasUppercase = /[A-Z]/.test(value);
    this.hasNumber = /[0-9]/.test(value);

    if (opts.containsNonAlphanumericCharacter) {
      const escaped = this.escapeForRegex(allowedChars);

      if (escaped) {
        const specialRegex = new RegExp(`[${escaped}]`);
        this.hasSpecialChar = specialRegex.test(value);
      } else {
        // Policy requires special chars but none are defined → mark as failing
        this.hasSpecialChar = false;
        console.warn('Password policy requires non-alphanumeric characters, but no allowed characters are defined.');
      }
    } else {
      this.hasSpecialChar = false;
    }
  }
}
